import { Theme } from '@mui/material/styles';
import { getPath } from '@mui/system';
import { themeTokens } from './tokens';
import { ColorToken, ComponentToken, ThemeTokens } from './types';

type ThemeSource = Theme | { theme: Theme };

type SpaceValue = keyof ThemeTokens['space'] | 0 | 'auto';

type RadiusValue = keyof ThemeTokens['radius'] | 0;

// Renders outside a ThemeProvider (e.g. isolated tests) get the default theme,
// which has no tokens; they fall back to the static token values.
const tokensOf = (source: ThemeSource): ThemeTokens =>
  ('tokens' in source ? source.tokens : source.theme?.tokens) ?? themeTokens;

export const getColor = (theme: Theme, token: ColorToken): string => {
  const color: unknown = getPath(theme.palette, token);
  return typeof color === 'string' ? color : '';
};

export const componentToken =
  (token: ComponentToken) => (source: ThemeSource) => {
    const value: unknown = getPath(tokensOf(source).components, token);
    return typeof value === 'string' || typeof value === 'number' ? value : '';
  };

export const space =
  (...values: SpaceValue[]) =>
  (source: ThemeSource) =>
    values
      .map((value) =>
        value === 0 || value === 'auto'
          ? String(value)
          : tokensOf(source).space[value],
      )
      .join(' ');

export const radius =
  (...values: RadiusValue[]) =>
  (source: ThemeSource) =>
    values
      .map((value) => (value === 0 ? '0' : tokensOf(source).radius[value]))
      .join(' ');

export const fontSize =
  (key: keyof ThemeTokens['fontSize']) => (source: ThemeSource) =>
    tokensOf(source).fontSize[key];

export const fontWeight =
  (key: keyof ThemeTokens['fontWeight']) => (source: ThemeSource) =>
    tokensOf(source).fontWeight[key];

export const lineHeight =
  (key: keyof ThemeTokens['lineHeight']) => (source: ThemeSource) =>
    tokensOf(source).lineHeight[key];

export const fontFamily =
  (key: keyof ThemeTokens['fontFamily']) => (source: ThemeSource) =>
    tokensOf(source).fontFamily[key];

export const shadow =
  (key: keyof ThemeTokens['shadow']) => (source: ThemeSource) =>
    tokensOf(source).shadow[key];

// States a look leaves empty are not drawn.
export const declaration = (property: string, value: string) =>
  value ? `${property}: ${value};` : '';

export const pressed = (background: string) =>
  background ? `:active:not(:disabled) { background: ${background}; }` : '';

export const getSeriesColors = (
  { palette }: Theme,
  index: number,
  count: number,
) => {
  const { seriesSequences } = palette.chart;
  const sequence =
    seriesSequences[Math.min(Math.max(count, 1), seriesSequences.length) - 1];
  return sequence[index % sequence.length];
};
